/**
 * We need to resolve the `false | undefined` logic below before converting this to *.ts
 * The `false` | `undefined` behavior is non-standard in our emotionStyled variants implementation.
 */
import {emotionStyled, tokens} from '@packages/utilities/styled';
import {HStack} from '../Stack';
import {FormLabel} from '../FormLabel';

export const RadioWrapperView = emotionStyled(HStack)(
  {
    gap: '$1',
  },
  {
    variants: {
      size: {
        medium: {
          gap: '$1',
        },
        'x-large': {
          gap: '$2',
        },
      },
    },
  }
);

export const RadioInputView = emotionStyled('input')({
  appearance: 'none',
  bottom: 0,
  left: 0,
  margin: 0,
  opacity: 0,
  position: 'absolute',
  right: 0,
  top: 0,
});

const BACKGROUND_CHECKED = '--local-radio-background-checked';
const BACKGROUND_CHECKED_HOVER = '--local-radio-background-checked-hover';
const BORDER_UNCHECKED = '--local-radio-border';
const BORDER_CHECKED = '--local-radio-border-checked';
const BORDER_CHECKED_HOVER = '--local-radio-border-checked-hover';

export const RadioButtonView = emotionStyled('div')(
  {
    backgroundColor: '$background1',
    borderColor: `var(${BORDER_UNCHECKED}, $uiCheckboxRadioBorderColor)`,
    borderRadius: '100%',
    borderStyle: 'solid',
    borderWidth: tokens.controls.borderWidth,
    color: 'white',
    display: 'grid',
    height: 12,
    placeItems: 'center',
    pointerEvents: 'none',
    width: 12,
    '&:hover': {
      backgroundColor: '$background2',
    },
    [`${RadioInputView}:focus-visible + &`]: {
      outlineColor: '$blueBorder',
      outlineStyle: 'solid',
      outlineWidth: 1,
      outlineOffset: 1,
    },
    '&::before': {
      backgroundColor: 'currentcolor',
      borderRadius: '100%',
      content: '""',
      display: 'none',
      height: 4,
      width: 4,
    },
  },
  {
    componentName: 'RadioButton',
    variants: {
      colorVariant: {
        blue: {
          [BACKGROUND_CHECKED]: tokens.colors.blueBackground,
          [BACKGROUND_CHECKED_HOVER]:
            tokens.colors.actionPrimaryBackgroundHover,
          [BORDER_UNCHECKED]: tokens.colors.uiCheckboxRadioBorderColor,
          [BORDER_CHECKED]: tokens.colors.actionPrimaryBackground,
          [BORDER_CHECKED_HOVER]: tokens.colors.actionPrimaryBackgroundHover,
        },
        purple: {
          [BACKGROUND_CHECKED]: tokens.colors.purpleBackground,
          [BACKGROUND_CHECKED_HOVER]: tokens.colors.purpleBackgroundHover,
          [BORDER_UNCHECKED]: tokens.colors.purpleBorder,
          [BORDER_CHECKED]: tokens.colors.purpleBackground,
          [BORDER_CHECKED_HOVER]: tokens.colors.purpleBackgroundHover,
        },
        green: {
          [BACKGROUND_CHECKED]: tokens.colors.greenBackground,
          [BACKGROUND_CHECKED_HOVER]: tokens.colors.greenBackgroundHover,
          [BORDER_UNCHECKED]: tokens.colors.greenBorder,
          [BORDER_CHECKED]: tokens.colors.greenBackground,
          [BORDER_CHECKED_HOVER]: tokens.colors.greenBackgroundHover,
        },
      },
      isChecked: {
        true: {
          backgroundColor: `var(${BACKGROUND_CHECKED})`,
          borderColor: `var(${BORDER_CHECKED})`,
          '&::before': {
            display: 'block',
          },
          '&:hover': {
            backgroundColor: `var(${BACKGROUND_CHECKED_HOVER})`,
            borderColor: `var(${BORDER_CHECKED_HOVER})`,
          },
        },
      },
      isError: {
        true: {
          [BACKGROUND_CHECKED]: tokens.colors.redBackground,
          [BACKGROUND_CHECKED_HOVER]: tokens.colors.redBackgroundHover,
          [BORDER_UNCHECKED]: tokens.colors.redBorder,
          [BORDER_CHECKED]: tokens.colors.redBackground,
          [BORDER_CHECKED_HOVER]: tokens.colors.redBackgroundHover,
        },
      },
      isDisabled: {
        true: {
          opacity: '$disabled',
        },
      },
    },
  }
);

export const RadioInputWrapperView = emotionStyled('div')({
  position: 'relative',
  display: 'inline-flex',
  alignItems: 'center',
});

export const RadioInputLabel = emotionStyled(FormLabel)(
  {},
  {
    componentName: 'RadioInputLabel',
    variants: {
      isDisabled: {
        true: {
          opacity: '$disabled',
        },
      },
    },
  }
);
